import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { baseURL, getTrash } from "../redux/Action";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import usePagination from "../Hooks/usePagination";
import { Pagination } from "@mui/material";
import { Navigate } from "react-router-dom";
import parse from "html-react-parser";
import { months } from "../Public/constants";

const TrashBlogs = () => {
  const { isLoading, isError, trash, isAuth, trashCount, token } = useSelector(
    (state) => state
  );

  const dispatch = useDispatch();

  let [page, setPage] = useState(1);
  const PER_PAGE = 4;

  const count = Math.ceil(trash.length / PER_PAGE);
  const { jump, currentData } = usePagination(trash, PER_PAGE);

  const handleChange = (e, p) => {
    setPage(p);
    jump(p);
  };

  useEffect(() => {
    document.title = "Blogshog | Blogs in Trash";

    getTrash(dispatch);
  }, []);

  if (!isAuth) {
    return <Navigate to={"/login"} />;
  }

  if (trashCount === 0 && !isLoading) {
    return (
      <section className="h-screen lg:h-[auto]">
        <p className="text-xl text-center mt-10 dark:text-white">
          No file in trash
        </p>
      </section>
    );
  }

  const handleRestore = (id) => {
    const toastId = toast.loading("Restoring...", { theme: "dark" });

    axios
      .delete(`${baseURL}/trash/${id}/restore`, {
        withCredentials: true,
        headers: {
          Authorization: token,
        },
      })
      .then((res) => {
        toast.update(toastId, {
          render: res.data.message,
          type: "success",
          isLoading: false,
          theme: "dark",
          autoClose: 2000,
          closeOnClick: true,
        });
        if (res.status === 200) {
          setTimeout(() => {
            getTrash(dispatch);
          }, 2000);
        }
      })
      .catch((err) => {
        toast.update(toastId, {
          render: "Something went wrong",
          type: "error",
          isLoading: false,
          autoClose: 2000,
          closeOnClick: true,
        });
      });
  };

  const handleDelete = (id) => {
    const toastId = toast.loading("Deleting...", { theme: "dark" });

    axios
      .delete(`${baseURL}/trash/${id}`, {
        withCredentials: true,
        headers: {
          Authorization: token,
        },
      })
      .then((res) => {
        toast.update(toastId, {
          render: res.data.message,
          type: "success",
          isLoading: false,
          theme: "dark",
          autoClose: 2000,
          closeOnClick: true,
        });
        if (res.status === 200) {
          setTimeout(() => {
            getTrash(dispatch);
          }, 2000);
        }
      })
      .catch((err) => {
        toast.update(toastId, {
          render: "Something went wrong",
          type: "error",
          isLoading: false,
          autoClose: 2000,
          closeOnClick: true,
        });
      });
  };

  return (
    <>
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            marginTop: "20px",
            height: "100vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Stack
          sx={{ width: "60%", margin: "2rem auto", height: "100vh" }}
          spacing={2}
        >
          <Alert severity="error">Something went wrong</Alert>
        </Stack>
      ) : (
        <>
          <section className="text-gray-600 body-font py-24  lg:w-[90%] mx-auto dark:text-white">
            <div className="container px-5 py-24 mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {currentData().map((el) => {
                  const date = el.createdAt.substring(8, 10);
                  const month_in_num = (el.createdAt.substring(5, 7) * 10) / 10;
                  const month = months[month_in_num - 1];
                  return (
                    <div
                      key={el._id}
                      className="py-8 px-4 shadow-lg border cursor-pointer dark:hover:bg-[#161721] shadow-gray-300 dark:shadow-none dark:border-none dark:bg-[#181924] rounded-[10px] mx-2 my-2"
                    >
                      <div className="h-full flex items-start">
                        <div className="w-12 flex-shrink-0 flex flex-col text-center leading-none">
                          <span className="text-gray-500 pb-2 mb-2 border-b-2 border-gray-200 dark:text-white">
                            {month}
                          </span>
                          <span className="font-medium text-lg text-gray-800 title-font leading-none dark:text-white">
                            {date}
                          </span>
                        </div>
                        <div className="flex-grow pl-6">
                          <h1 className="title-font text-xl font-medium text-gray-900 mb-3 dark:text-white">
                            {el.title}
                          </h1>
                          <div className="leading-relaxed mb-5 dark:text-gray-300">
                            {" "}
                            {parse(el.body.slice(0, 25))}...
                          </div>
                          <p className="inline-flex items-center">
                            <img
                              alt="blog"
                              src={el.user?.avatar}
                              className="w-8 h-8 rounded-full flex-shrink-0 object-cover object-center"
                            />
                            <span className="flex-grow flex flex-col pl-3">
                              <span className="title-font font-medium text-gray-900 dark:text-white">
                                {el.user.name}
                              </span>
                            </span>
                          </p>
                          <div className="flex justify-start">
                            <button
                              className="flex flex-wrap mr-2 my-2 text-white bg-[#1D4ED8] border-0 py-2 px-8 focus:outline-none hover:bg-indigo-600 rounded text-[14px]"
                              onClick={() => handleRestore(el._id)}
                            >
                              Restore
                            </button>
                            <button
                              className="flex flex-wrap mx-2 my-2 text-white bg-[#1D4ED8] border-0 py-2 px-8 focus:outline-none hover:bg-indigo-600 rounded text-[14px]"
                              onClick={() => handleDelete(el._id)}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {trashCount > 4 && (
              <div className="flex justify-center items-center mb-16">
                <Stack spacing={2}>
                  <Pagination
                    count={count}
                    variant="outlined"
                    shape="rounded"
                    page={page}
                    onChange={handleChange}
                  />
                </Stack>
              </div>
            )}
          </section>
        </>
      )}
    </>
  );
};

export default TrashBlogs;
